import styled from "@emotion/styled";
import { spacing, Stack } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Box,
  useTheme,
  useMediaQuery,
  Divider as MuiDivider,
  Paper,
  Badge,
  BadgeProps,
  Chip,
  linearProgressClasses,
  Typography,
  Button
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { IDocument } from "../../../../types/document";
import { DialogMode } from "../../../../types/dialogmode";
import { ISignature, ISignee } from "../../../../types/signature";
import { AIReadingStatus, IContract } from "../../../../types/contract";
import { v4 as uuidv4 } from "uuid";

import useAuth from "../../../../hooks/useAuth";
import AskMeAnything from "../../../components/AskMeAnything";
import { AttachFile, Gavel } from "@mui/icons-material";

import {
  useAddHumanDataVerificationMutation,
  useGetHumanDataVerificationsQuery,
  useGetPromptsQuery
} from "../../../../redux/slices/indexApiSlice";
import { IPrompt } from "../../../../types/prompt";
import {
  ContractExtractionResult,
  Paragraph
} from "../../../../types/aiOutput";
import { LinearProgressWithLabel } from "../../dashboards/Analytics/PromptsSideBar";
import { red } from "@mui/material/colors";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import ArticleAccordion from "./ArticleAccordion";
// import InsightsAnalysis from './InsightsAndAnalysis';
import TabContentRenderer from "./AIContractDetails/TabContentRenderer";
import InsightsSummaryCards from "./AIContractDetails/InsightsSummaryCards";
import TabsRenderer from "./AIContractDetails/TabsRenderer";
import { INSIGHTS_ANALYSIS_TAB_ITEMS } from "./AIContractDetails/types";

const Divider = styled(MuiDivider)(spacing);

export const ScoreBar = styled(LinearProgressWithLabel)(({ theme }) => ({
  height: 14,
  borderRadius: 7,

  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "transparent"
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 7,
    backgroundColor: red[600]
  }
}));

const StyledBadge = styled(Badge)<BadgeProps>(({ theme, hidden }) => ({
  "& .MuiBadge-badge": {
    right: -3,
    top: 11,
    border: hidden ? "none" : `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px"
  }
}));
type AIContractDetailSidebarProps = {
  mode: DialogMode;
  signer?: ISignee;
  signees?: ISignee[];
  contract?: IContract;
  loading?: boolean;
  documents: IDocument[];
  document: IDocument;
  aiOutputs: ContractExtractionResult[];
  analysisStatus?: AIReadingStatus;
  onAnalysis?: () => void;
  setSignees?: React.Dispatch<React.SetStateAction<Array<ISignee>>>;
  setSignatures?: React.Dispatch<React.SetStateAction<Array<ISignature>>>;
  signatures?: ISignature[];
  resetSignatures?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchContract?: () => void;
  setSearchForText?: (text: string) => void;
  promptIdToExpand?: string;
  handlePromptClick?: (prompt?: IPrompt) => void;
  setAiOutput?: React.Dispatch<
    React.SetStateAction<ContractExtractionResult | undefined>
  >;
  setSelectedParagraph: React.Dispatch<
    React.SetStateAction<Paragraph | undefined>
  >;
  sideBarTab: INSIGHTS_ANALYSIS_TAB_ITEMS;
  setSideBarTab: React.Dispatch<
    React.SetStateAction<INSIGHTS_ANALYSIS_TAB_ITEMS>
  >;
  switchToDocument: (documentId: string) => void;
};

export const DocumentChip = ({ ...props }: { document?: IDocument }) => {
  const { document } = props;

  return (
    <Chip
      label={document?.name}
      icon={document?.type === "attachment" ? <AttachFile /> : <Gavel />}
      variant="outlined"
      color="primary"
    />
  );
};

const AIContractDetailSidebar: React.FC<AIContractDetailSidebarProps> = ({
  mode,
  signer,
  signees,
  contract,
  documents,
  document,
  aiOutputs,
  analysisStatus,
  onAnalysis,
  setSignees,
  setSignatures,
  signatures,
  resetSignatures,
  handlePromptClick,
  promptIdToExpand,
  setAiOutput,
  setSelectedParagraph,
  sideBarTab,
  setSideBarTab,
  switchToDocument,
  loading
}) => {
  const { user } = useAuth();
  const theme = useTheme();

  const [badges, setBadges] = useState<{ [key: string]: number }>({});

  const [addVerification] = useAddHumanDataVerificationMutation();

  const { data: prompts = [], isSuccess: promptsLoaded } = useGetPromptsQuery();
  const { data: verifications } = useGetHumanDataVerificationsQuery(
    contract ?? skipToken
  );

  // const [aiOutputs, setAiOutputs] = useState<ContractExtractionResult[]>([]);

  const isDownLg = useMediaQuery(theme.breakpoints.down("lg"));
  const [expanded, setExpanded] = useState<string>();

  const [suggestedPrompts, setSuggestedPrompts] = useState<
    Partial<IPrompt & { relavantParagraphs: Paragraph[] }>[]
  >([]);
  const [promptsSuggestedCount, setPromptsSuggestedCount] = useState<number>(0);

  useEffect(() => {
    const newSuggestedPrompts = aiOutputs
      .map((a) => a.suggestedPrompts)
      .flat()
      .filter((p) => p);

    const withIds = newSuggestedPrompts.map((p) => {
      // generate a randomguid
      const id = uuidv4();

      return {
        ...p,
        id,
        body: p?.body || "",
        author: user.id
      };
    });

    setSuggestedPrompts(withIds);
  }, [aiOutputs]);

  const handleTabChange = (value: INSIGHTS_ANALYSIS_TAB_ITEMS) => {
    setSideBarTab(value);
  };

  const setBadge = (tabIndex: string, num: number) => {
    setBadges((prev) => ({ ...prev, [tabIndex]: num }));
  };

  const selectParagraph = (
    paragraph?: Paragraph,
    prompt?: IPrompt,
    aiOutput?: ContractExtractionResult
  ) => {
    if (!prompt) {
      setExpanded(undefined);

      if (handlePromptClick) {
        handlePromptClick(undefined);
      }
      return;
    }

    // check if we need to switch to another document
    if (aiOutput?.documentId && document.id !== aiOutput?.documentId) {
      switchToDocument(aiOutput.documentId);

      setTimeout(() => {
        setExpanded(prompt.id);
        setSelectedParagraph(paragraph);
        if (setAiOutput) {
          setAiOutput(aiOutput);
        }

        if (handlePromptClick) {
          handlePromptClick(prompt);
        }
      }, 1300);
    } else {
      setExpanded(prompt.id);
      setSelectedParagraph(paragraph);
      if (setAiOutput) {
        setAiOutput(aiOutput);
      }

      if (handlePromptClick) {
        handlePromptClick(prompt);
      }
    }
  };

  useEffect(() => {
    if (promptIdToExpand) {
      // findHighlight(null, true, promptFind);
      setExpanded(promptIdToExpand);
    }
  }, [promptIdToExpand]);

  // useEffect(() => {
  //   const sorted = aiOutputsMainContract
  //     .concat(aiOutputsAttachments)
  //     .sort((a, b) => {
  //       // sort the aiOutputs by date
  //       return (
  //         new Date(b.processedAt).getTime() - new Date(a.processedAt).getTime()
  //       );
  //     });

  //   setAiOutputs(sorted);
  // }, [aiOutputsMainContract, aiOutputsAttachments]);

  // useEffect(() => {
  //   if (attachments && attachments?.length > 0) {
  //     attachments.forEach((attachment) => {
  //       getAttachmentAIOutput(attachment)
  //         .unwrap()
  //         .then((result) => {
  //           if (result) {
  //             // add the result to the aiOutputs
  //             const newAIOutputs = aiOutputsAttachments.concat(result);
  //             setAIOutputAttachments(newAIOutputs);
  //           }
  //         });
  //     });
  //   } else {
  //     setAIOutputAttachments([]);
  //   }
  // }, [attachments]);

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        mb: 6,
        py: 3,
        px: 3,

        backgroundColor: "background.default",
        minHeight: 450,
        overflow: "auto",
        maxHeight: "calc(100vh - 200px)"
      }}
    >
      <Grid container direction="column" gap={3}>
        {contract && (
          <>
            <InsightsSummaryCards onClick={handleTabChange} />
            <Paper>
              <Stack direction="column" gap={4}>
                <TabsRenderer
                  onChange={handleTabChange}
                  tabValue={sideBarTab}
                />
                <TabContentRenderer
                  sideBarTab={sideBarTab}
                  contract={contract}
                  mode={mode}
                  signer={signer}
                  signees={signees}
                  signatures={signatures}
                  document={document}
                  documents={documents}
                  aiOutputs={aiOutputs}
                  analysisStatus={analysisStatus}
                  onAnalysis={onAnalysis}
                  setSignees={setSignees}
                  setSignatures={setSignatures}
                  resetSignatures={resetSignatures}
                  setBadge={setBadge}
                  badges={badges}
                  isDownLg={isDownLg}
                  prompts={prompts}
                  selectParagraph={selectParagraph}
                  expanded={expanded}
                  verifications={verifications}
                  suggestedPrompts={suggestedPrompts}
                />
              </Stack>
            </Paper>
          </>
        )}

        {sideBarTab === INSIGHTS_ANALYSIS_TAB_ITEMS.AI_ASSISTANT && (
          <AskMeAnything
            showLabel
            placeholder={"Ask a question about this contract"}
          />
        )}

        {aiOutputs?.length > 0 && aiOutputs[0].articles?.length > 0 && (
          <Box display="flex" flexDirection="column" gap={0}>
            {/* <Typography variant="h6" sx={{ mb: 2 }}>
              {t("Fields found")}
            </Typography> */}

            {aiOutputs?.length > 0 &&
              aiOutputs[0].articles.map((article) => {
                return (
                  <ArticleAccordion
                    key={`articleAccordion-${article.id}`}
                    article={article as any}
                    isExpanded={true}
                    mode={mode}
                    selectParagraph={selectParagraph}
                  />
                );
              })}
          </Box>
        )}
        {/*
        //replace with hover button bottom right
         <Box mb={5}>
          <AskMeAnything placeholder={'Ask a question about this contract'} />
        </Box> */}
      </Grid>
    </Paper>
  );
};

export default AIContractDetailSidebar;
